import { IActionFn, IPageData } from '@/store/types';
import { IModuleState } from './types';
import { $http } from '@/axios';
import store from '@/store';

// 查询企业/会员列表
export const queryOrgs: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setOrgs', { ...state.list, loading: true });
    const res: IPageData = await $http.post('/v1/data/system/org/pagelist', payload);
    commit('setOrgs', { data: res.list, total: res.total, pageNum: res.pageNum, pageSize: res.pageSize });
  } catch (e) {
    commit('setOrgs', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询企业/会员详情
// 取消权限控制，默认给出本组织信息，如果指定 orgId 为非本组织或非本组织的子组织，则返回无权限异常
export const queryOrgDetail: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOrgDetail', { data: null, loading: true });
    const res = await $http.post('/v1/data/system/org/detail', { orgId: payload });
    commit('setOrgDetail', { data: res });
  } catch (e) {
    commit('setOrgDetail', { data: null });
  }
};

// 查询客户类型列表
export const queryOrgAdditionalTypes: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setOrgAdditionalTypes', { ...state.orgAdditionalTypes, loading: true });
    const res = await $http.get('/common/chooseoptions/OrgAdditionalType');
    commit('setOrgAdditionalTypes', { data: res });
  } catch (e) {
    commit('setOrgAdditionalTypes', { data: [] });
  }
};

// 添加会员/企业
export const addOrg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOrgInfo', { data: null, loading: true });
    const res: any = await $http.post('/v1/system/org/add', payload);
    commit('setOrgInfo', { data: { id: res?.orgId } });
    store.commit('common/setToast', { visible: true, text: '会员/企业新增成功' });
    return true;
  } catch (e) {
    commit('setOrgInfo', { data: null });
    return false;
  }
};

// 升级普通会员(到期后降级的会员，或自注册的会员升级为收费会员)
export const upgradeOrg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOrgInfo', { data: null, loading: true });
    const res: any = await $http.post('/v1/system/org/modifytoupgrade', payload);
    commit('setOrgInfo', { data: { id: res?.orgId } });
    store.commit('common/setToast', { visible: true, text: '升级成功' });
    return true;
  } finally {
    commit('setLoading', false);
  }
};

// 添加试用会员
export const addTrialMember: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', true);
    await $http.post('/v1/system/org/addtrial', payload);
    return true;
  } catch (e) {
    return false;
  } finally {
    commit('setLoading', false);
  }
};

// 编辑企业基础信息
export const editOrg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', false);
    await $http.post('/v1/system/org/modify', payload);
    store.commit('common/setToast', { visible: true, text: '会员/企业编辑成功' });
    return true;
  } catch (e) {
    return false;
  } finally {
    commit('setLoading', false);
  }
};

// 是否允许创建子组织
export const supportSubOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    // await $http.post('baseinfo/enterprise/set/restricted', payload);
    await $http.post('/v1/check/org/supportsuborg', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 锁定会员/企业
export const lockOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const isMember = !!payload.memberId;
    // const postUrl = isMember ? '/baseinfo/member/lock' : '/baseinfo/enterprise/lock';
    const postUrl = isMember ? '/v1/system/member/lock' : '/v1/system/org/lock';
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post(postUrl, payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 激活会员/企业
export const activeOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/baseinfo/enterprise/activation', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除会员/企业
export const deleteOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    // payload 里, id 和 orgId 不可能同时存在
    // payload.id 如果存在, 表示当前删除的是会员, 此时的 id 是会员Id
    // payload.orgId 如果存在, 表示当前删除的是企业, 此时 orgId 是组织Id
    const isMember = !!payload.id;
    // const postUrl = isMember ? '/baseinfo/member/delete' : '/baseinfo/enterprise/delete';
    const postUrl = isMember ? '/v1/system/member/delete' : '/v1/system/org/delete';
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post(postUrl, payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 检查用户登录名是否重复
export const checkMember: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    // const notExist = await $http.post('/check/member/notexisted', payload);
    const notExist = await $http.post('/v1/check/user/isloginvalid', payload);
    return { data: notExist, msg: notExist ? '' : '会员登录名称已被占用，请更换' };
  } catch (e) {
    return { data: false, msg: '会员登录名称检查失败' };
  }
};

// 检查组织下是否有活跃用户和子组织
export const checkOrg: IActionFn<IModuleState> = ({ commit, state }, payload) => {
  // const fetchUsers = $http.post('/baseinfo/users', { pageNum: 1, pageSize: 1, status: [1], orgId: payload });
  // const fetchSubOrgs = $http.post('baseinfo/enterprise/suborgs/search', { pageNum: 1, pageSize: 1, orgId: payload });
  const fetchUsers = $http.post(
    '/v1/baseinfo/user/pagelist',
    { pageNum: 1, pageSize: 1, status: [1], orgId: payload },
  );
  const fetchSubOrgs = $http.post(
    '/v1/system/suborg/search',
    { pageNum: 1, pageSize: 1, orgId: payload },
  );
  commit('setOrgChecked', { data: false, loading: true });

  Promise.all([fetchUsers, fetchSubOrgs]).then((results) => {
    const [users, subOrgs] = (results || []) as unknown as IPageData[];
    const userTotal = users?.total || 0;
    const subTotal = subOrgs?.total || 0;
    commit('setOrgChecked', { data: !!userTotal || !!subTotal });
  }).catch(() => {
    commit('setOrgChecked', { data: false });
  });
};

// 检查组织的总账号是否小于已使用账号数量
export const checkUserLimit: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    // true 表示超过
    commit('setAlert', { loading: true });
    const overcapacity = await $http.post('/v1/data/system/org/overcapacity', payload);
    if (!!overcapacity) {
      const content = '账号总数量小于已使用账号数量，提交后所有账号都会被删除！！！';
      commit('setAlert', { loading: false, visible: true, content });
    } else {
      commit('setAlert', { loading: false });
    }
  } catch (e) {
    commit('setAlert', { loading: false, visible: true, content: '检查出错' });
  }
};

// 开通会员/企业(创建订单)
export const createOrder: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOrderInfo', { data: null, loading: true });
    const res = await $http.post('/v1/finance/org/serviceadjust', payload);
    commit('setOrderInfo', { data: res });
    // store.commit('common/setToast', { visible: true, text: '订单创建成功' });
    return true;
  } catch (e) {
    commit('setOrderInfo', { data: null });
    return false;
  }
};

// 支付订单
export const payOrder: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    const res = await $http.post('/v1/finance/org/addpaymentandpay', payload);
    commit('setOrgInfo', { data: res });
    store.commit('common/setToast', { visible: true, text: '支付成功' });
    return true;
  } catch (e) {
    return false;
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 计算价格
export const calcPrice: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setPrice', { data: null, loading: true });
    const res: any = await $http.post('/v1/finance/org/calcserviceprice', payload);
    commit('setPrice', { data: res?.totalAmount == null ? null : res.totalAmount });
  } catch (e) {
    commit('setPrice', { data: null });
  }
};

// 查询沟通记录
export const queryExchanges: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setExchanges', { ...state.exchanges, loading: true });
    // const res: IPageData = await $http.post('/v1/data/operation/customerservice/exchangepagelist', payload);
    const res: IPageData = await $http.post('/v1/operation/csnotification/list', payload);
    commit('setExchanges', { data: res.list, total: res.total, pageNum: res.pageNum, pageSize: res.pageSize });
  } catch (e) {
    commit('setExchanges', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 添加沟通记录
export const addExchange: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', true);
    // await $http.post('/v1/operation/customerservice/addexchange', payload);
    await $http.post('/v1/operation/csnotification/add', payload);
    store.commit('common/setToast', { visible: true, text: '成功添加沟通记录' });
  } finally {
    commit('setLoading', false);
  }
};

// 查询组织中已授权品牌列表
export const queryOrgAuthedBrands: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setOrgAuthedBrands', { ...state.authedBrands, loading: true });
    // const { list, pageNum, pageSize, total } = await $http.post('/baseinfo/organizations/brand/detail', payload);
    const { list, pageNum, pageSize, total } = await $http.post('/v1/system/org/authbrands', payload);
    commit('setOrgAuthedBrands', { data: list, pageNum, pageSize, total });
  } catch (e) {
    commit('setOrgAuthedBrands', { data: [], pageNum: 0, pageSize: 0, total: 0 });
  }
};

// 为某个组织添加授权品牌
export const addOrgAuthBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    // await $http.post('/authorization/ref/brand/create', payload);
    await $http.post('/v1/system/org/authbrand/add', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 为某个组织删除授权品牌
export const deleteOrgAuthBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    // await $http.post('/authorization/org/ref/delete', payload);
    await $http.post('/v1/system/org/authbrand/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 查询竞品品牌列表
export const queryOrgCompetitors: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setOrgCompetitors', { ...state.competitors, loading: true });
    // const res: IPageData = await $http.post('data/org/competitive/brand/list', payload);
    const res: IPageData = await $http.post('/v1/system/org/competitivebrands', payload);
    commit('setOrgCompetitors', { data: res.list, pageNum: res.pageNum, pageSize: res.pageSize, total: res.total });
  } catch (e) {
    commit('setOrgCompetitors', { data: [], pageNum: 0, pageSize: 0, total: 0 });
  }
};

// 为某个组织添加竞品品牌
export const addCompetitor: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    // await $http.post('/competitive/brand/batch/add', payload);
    await $http.post('/v1/system/org/competitivebrand/add', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 为某个组织删除竞品品牌
export const deleteCompetitor: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    // await $http.post('/competitive/brand/batch/delete', payload);
    await $http.post('/v1/system/org/competitivebrand/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};
